import { CircleAlert } from "lucide-react";
import { EditableContractHeader } from "@/components/copy-operations/EditableContractHeader";
import { EditableMovements } from "@/components/copy-operations/EditableMovements";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import type { UnresolvedRef } from "@/lib/coach-manager/transform";
import type { Contract } from "@/lib/coach-manager/types";

interface CopyOperationStagingEditorProps {
  contract: Contract;
  targetBuId: string;
  unresolvedRefs: UnresolvedRef[];
  onChange: (contract: Contract) => void;
  disabled?: boolean;
  /** Hide the "before posting" unresolved-refs checklist — set to `false` once the
   * operation is no longer editable (completed/in-progress), where that language
   * no longer applies. Defaults to shown. */
  showUnresolvedPanel?: boolean;
  acknowledgedUnresolved: boolean;
  onAcknowledgedUnresolvedChange: (value: boolean) => void;
}

/**
 * The editable version of `ContractDetail` — same layout, but every field is
 * an input and every master/reference-data column is a `MasterDataSelect`
 * dropdown sourced from the target BU. `unresolvedRefs` is shown as a static
 * "needs attention" checklist from the initial transform; there's no
 * separate resolve action — the same fields are directly editable below.
 * When any refs are unresolved, the caller gates its commit button on
 * `acknowledgedUnresolved` — never a silent default.
 */
export function CopyOperationStagingEditor({
  contract,
  targetBuId,
  unresolvedRefs,
  onChange,
  disabled,
  showUnresolvedPanel = true,
  acknowledgedUnresolved,
  onAcknowledgedUnresolvedChange,
}: CopyOperationStagingEditorProps) {
  return (
    <div className="space-y-6">
      {showUnresolvedPanel && unresolvedRefs.length > 0 && (
        <div className="space-y-2 rounded-xl border border-dashed p-4">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Badge variant="destructive">{unresolvedRefs.length}</Badge>
            Reference{unresolvedRefs.length === 1 ? "" : "s"} with no mapping to this target BU yet
          </p>
          <p className="text-xs text-muted-foreground">
            These fields kept their source value below — pick the right target value for each
            occurrence in the sections below before posting.
          </p>
          <ul className="flex flex-wrap gap-2 pt-1">
            {unresolvedRefs.map((ref) => (
              <li key={`${ref.entityType}:${ref.sourceRefId}`}>
                <Badge variant="outline">
                  {ref.entityType}: {ref.sourceRefId} ({ref.occurrences}×)
                </Badge>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2 pt-2 text-sm">
            <Checkbox
              id="ack-unresolved-refs"
              checked={acknowledgedUnresolved}
              onCheckedChange={(checked) => onAcknowledgedUnresolvedChange(checked === true)}
              disabled={disabled}
            />
            <label htmlFor="ack-unresolved-refs">
              I've reviewed these unresolved references and either fixed the values below or intend
              to keep the source value.
            </label>
          </div>
        </div>
      )}

      <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <span className="text-destructive">*</span> required by Coach Manager
        <CircleAlert className="ml-3 size-3 shrink-0 text-amber-500" />
        transformed to a target-BU-specific value on copy
      </p>

      <EditableContractHeader
        contract={contract}
        targetBuId={targetBuId}
        disabled={disabled}
        onChange={onChange}
      />

      <EditableMovements
        movements={contract.ContractMovements ?? []}
        targetBuId={targetBuId}
        disabled={disabled}
        onChange={(movements) => onChange({ ...contract, ContractMovements: movements })}
      />
    </div>
  );
}
